import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import useListKeyboardNav, { LIST_ROW_CLASS } from '@/hooks/useListKeyboardNav';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const EMPTY = {
  code: '',
  label: '',
  short_label: '',
  position: 80,
  counts_as_taught: false,
  is_online: false,
  consumes_prepaid: false,
  is_cancellation: true,
  refund_quotient: 0,
  requires_makeup: false,
  requires_new_date: false,
  notify_parents: false,
  teacher_status: 'absent_excused',
  is_active: true,
};

const STATUS_OPTIONS = [
  { v: 'present', l: 'Anwesend (present)' },
  { v: 'absent_excused', l: 'Entschuldigt (EA)' },
  { v: 'absent_unexcused', l: 'Unentschuldigt (UA)' },
  { v: 'none', l: 'kein Matrix-Status' },
];

function Toggle({ checked, onChange, label, testId }) {
  return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <Switch checked={!!checked} onCheckedChange={onChange} data-testid={testId} />
      {label}
    </label>
  );
}

export default function AttendanceCategoriesTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const openEdit = (row) => setEditor({
    ...row,
    refund_quotient: row.refund_quotient ?? 0,
    teacher_status: row.teacher_status || 'none',
  });

  const { getRowProps } = useListKeyboardNav({
    items: rows,
    onSelect: openEdit,
    enabled: !editor && !toDelete,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/attendance-categories');
      setRows(r.data || []);
    } catch {
      toast.error('Anwesenheitsarten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editor.label?.trim()) { toast.error('Bezeichnung ist erforderlich'); return; }
    if (!editor.id && !editor.code?.trim()) { toast.error('Kürzel ist erforderlich'); return; }
    const payload = {
      code: (editor.code || '').trim().toLowerCase(),
      label: editor.label.trim(),
      short_label: (editor.short_label || '').trim(),
      position: Number(editor.position) || 0,
      counts_as_taught: !!editor.counts_as_taught,
      is_online: !!editor.is_online,
      consumes_prepaid: !!editor.consumes_prepaid,
      is_cancellation: !!editor.is_cancellation,
      refund_quotient: Number(editor.refund_quotient) || 0,
      requires_makeup: !!editor.requires_makeup,
      requires_new_date: !!editor.requires_new_date,
      notify_parents: !!editor.notify_parents,
      teacher_status: editor.teacher_status === 'none' ? null : editor.teacher_status,
      is_active: editor.is_active !== false,
    };
    try {
      if (editor.id) {
        await api.put(`/admin/attendance-categories/${editor.id}`, payload);
        toast.success('Anwesenheitsart aktualisiert');
      } else {
        await api.post('/admin/attendance-categories', payload);
        toast.success('Anwesenheitsart angelegt');
      }
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/admin/attendance-categories/${toDelete.id}`);
      toast.success('Anwesenheitsart deaktiviert');
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Deaktivieren fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-3" data-testid="attendance-categories-tab">
      <p className="text-sm text-slate-600">
        Anwesenheits- und Ausfallarten für diesen Mandanten. Jede Kategorie legt fest,
        ob der Unterricht als erteilt gilt, eine Karteneinheit verbraucht wird,
        eine Erstattung entsteht oder ein Nachholtermin nötig ist.
      </p>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Anwesenheitsarten</h2>
          <Button size="sm" variant="outline" onClick={() => setEditor({ ...EMPTY })} data-testid="add-attendance-category-btn">
            <Plus className="w-4 h-4 mr-1" /> Neue Art
          </Button>
        </div>
        {loading ? (
          <div className="p-10 flex justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-red" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wide">
                <tr>
                  <th className="px-3 py-2 font-semibold">Art</th>
                  <th className="px-3 py-2 font-semibold">Folgen</th>
                  <th className="px-3 py-2 font-semibold text-center">Erstattung</th>
                  <th className="px-3 py-2 font-semibold w-20" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-slate-400">Noch keine Anwesenheitsarten</td>
                  </tr>
                )}
                {rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className={LIST_ROW_CLASS}
                    data-testid={`attendance-category-row-${row.code}`}
                    {...getRowProps(idx, row)}
                  >
                    <td className="px-3 py-2">
                      <span className="font-medium">{row.label}</span>
                      <span className="ml-1.5 text-[10px] text-slate-400">{row.short_label || row.code}</span>
                      {!row.is_active && <span className="ml-1.5 text-[10px] text-slate-400">inaktiv</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{row.consequence || '—'}</td>
                    <td className="px-3 py-2 text-center text-xs">
                      {row.refund_quotient > 0 ? `${Math.round(row.refund_quotient * 100)} %` : '—'}
                    </td>
                    <td className="px-3 py-2">
                      {row.is_active && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                                onClick={() => setToDelete(row)} title="Deaktivieren">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editor} onOpenChange={(o) => { if (!o) setEditor(null); }}>
        <DialogContent className="max-w-lg" data-testid="attendance-category-editor">
          <DialogHeader>
            <DialogTitle>{editor?.id ? 'Anwesenheitsart bearbeiten' : 'Neue Anwesenheitsart'}</DialogTitle>
          </DialogHeader>
          {editor && (
            <div className="space-y-3 mt-1">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <Label>Bezeichnung *</Label>
                  <Input value={editor.label} onChange={(e) => setEditor({ ...editor, label: e.target.value })}
                         data-testid="attendance-category-label" />
                </div>
                <div>
                  <Label>Kürzel</Label>
                  <Input value={editor.short_label} onChange={(e) => setEditor({ ...editor, short_label: e.target.value })}
                         data-testid="attendance-category-short" />
                </div>
              </div>
              {!editor.id && (
                <div>
                  <Label>Code *</Label>
                  <Input value={editor.code} onChange={(e) => setEditor({ ...editor, code: e.target.value })}
                         placeholder="z. B. krank" data-testid="attendance-category-code" />
                </div>
              )}
              <div>
                <Label>Matrix-Status</Label>
                <Select value={editor.teacher_status || 'none'}
                        onValueChange={(v) => setEditor({ ...editor, teacher_status: v })}>
                  <SelectTrigger data-testid="attendance-category-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((o) => (
                      <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Erstattungsquotient (0–1)</Label>
                <Input type="number" min="0" max="1" step="0.1"
                       value={editor.refund_quotient}
                       onChange={(e) => setEditor({ ...editor, refund_quotient: e.target.value })}
                       data-testid="attendance-category-refund" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Toggle label="Gilt als erteilt" checked={editor.counts_as_taught}
                        onChange={(v) => setEditor({ ...editor, counts_as_taught: v })}
                        testId="attendance-category-taught" />
                <Toggle label="Online" checked={editor.is_online}
                        onChange={(v) => setEditor({ ...editor, is_online: v })}
                        testId="attendance-category-online" />
                <Toggle label="Verbraucht Karteneinheit" checked={editor.consumes_prepaid}
                        onChange={(v) => setEditor({ ...editor, consumes_prepaid: v })}
                        testId="attendance-category-prepaid" />
                <Toggle label="Ausfall (erscheint im Menü)" checked={editor.is_cancellation}
                        onChange={(v) => setEditor({ ...editor, is_cancellation: v })}
                        testId="attendance-category-cancel" />
                <Toggle label="Nachholpflicht" checked={editor.requires_makeup}
                        onChange={(v) => setEditor({ ...editor, requires_makeup: v })}
                        testId="attendance-category-makeup" />
                <Toggle label="Neuer Termin nötig" checked={editor.requires_new_date}
                        onChange={(v) => setEditor({ ...editor, requires_new_date: v })}
                        testId="attendance-category-new-date" />
                <Toggle label="Eltern informieren" checked={editor.notify_parents}
                        onChange={(v) => setEditor({ ...editor, notify_parents: v })}
                        testId="attendance-category-notify" />
                <Toggle label="Aktiv" checked={editor.is_active !== false}
                        onChange={(v) => setEditor({ ...editor, is_active: v })}
                        testId="attendance-category-active" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Abbrechen</Button>
            <Button onClick={save} data-testid="save-attendance-category-btn">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Anwesenheitsart deaktivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{toDelete?.label}</strong> erscheint danach nicht mehr in der App.
              Bereits erfasste Termine bleiben erhalten.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Deaktivieren</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
